import { useState } from "react";
import { HardHat, FilePdf } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useLang } from "@/i18n";
import SectionHeader from "@/components/SectionHeader";
import HseReportDialog from "@/components/hse/HseReportDialog";
import HseDashboard from "@/components/hse/HseDashboard";
import DailyReportModule from "@/components/hse/DailyReportModule";
import IperModule from "@/components/hse/IperModule";
import BowTieModule from "@/components/hse/BowTieModule";
import JsaModule from "@/components/hse/JsaModule";
import PermitsModule from "@/components/hse/PermitsModule";
import ObservationsModule from "@/components/hse/ObservationsModule";
import ToolboxModule from "@/components/hse/ToolboxModule";
import InvestigationsModule from "@/components/hse/InvestigationsModule";
import ActionsModule from "@/components/hse/ActionsModule";
import NormativeAuditModule from "@/components/hse/NormativeAuditModule";
import HseKpiTargets from "@/components/hse/HseKpiTargets";
import MedicalTestsModule from "@/components/hse/MedicalTestsModule";
import EppModule from "@/components/hse/EppModule";
import DriverLicensesModule from "@/components/hse/DriverLicensesModule";
import Safety from "@/pages/Safety";
import SupervisorProgramModule from "@/components/hse/SupervisorProgramModule";

// Iter120: agrupación visual de las 15 secciones de SGSSO en 5 bloques
// funcionales para dar un aspecto más limpio y ordenado. Antes eran 15
// tabs sueltas sin jerarquía → difícil ubicar cada módulo.
const TAB_GROUPS = [
  {
    key: "panel",
    label: "Panel",
    color: "bg-blue-50 border-blue-200 text-blue-900",
    tabs: [
      { v: "dashboard", l: "hseTabKpis", C: HseDashboard },
      { v: "targets", l: "hseTabTargets", C: HseKpiTargets },
    ],
  },
  {
    key: "operacion",
    label: "Operación diaria",
    color: "bg-orange-50 border-orange-200 text-orange-900",
    tabs: [
      { v: "daily", l: "hseTabDaily", C: DailyReportModule },
      { v: "supervisor", l: "hseTabSupervisor", C: SupervisorProgramModule },
      { v: "observations", l: "hseTabObservations", C: ObservationsModule },
      { v: "toolbox", l: "hseTabToolbox", C: ToolboxModule },
      { v: "permits", l: "hseTabPermits", C: PermitsModule },
    ],
  },
  {
    key: "riesgo",
    label: "Análisis de riesgo",
    color: "bg-amber-50 border-amber-200 text-amber-900",
    tabs: [
      { v: "iper", l: "hseTabIper", C: IperModule },
      { v: "bowtie", l: "hseTabBowtie", C: BowTieModule },
      { v: "jsa", l: "hseTabJsa", C: JsaModule },
      { v: "investigations", l: "hseTabInvestigations", C: InvestigationsModule },
    ],
  },
  {
    key: "cumplimiento",
    label: "Cumplimiento",
    color: "bg-emerald-50 border-emerald-200 text-emerald-900",
    tabs: [
      { v: "actions", l: "hseTabActions", C: ActionsModule },
      { v: "normative-audit", l: "hseTabNormAudit", C: NormativeAuditModule },
      { v: "medical", l: "hseTabMedical", C: MedicalTestsModule },
      { v: "epp", l: "hseTabEpp", C: EppModule },
      { v: "driver-licenses", l: "hseTabDriverLicenses", C: DriverLicensesModule },
    ],
  },
  {
    key: "legacy",
    label: "Otros",
    color: "bg-slate-50 border-slate-200 text-slate-700",
    tabs: [
      { v: "legacy", l: "hseTabLegacy", C: Safety },
    ],
  },
];
const TABS = TAB_GROUPS.flatMap((g) => g.tabs);

export default function HSE() {
  const { t } = useLang();
  const [reportOpen, setReportOpen] = useState(false);
  return (
    <div className="space-y-6">
      <SectionHeader
        icon={HardHat}
        title={t("hse")}
        eyebrow={t("hseSubtitle")}
        size="lg"
        testid="hse-header"
        right={
          <>
            <Button data-testid="hse-reports-button" variant="outline"
                    className="gap-2 border-slate-300 text-slate-800 hover:bg-slate-100"
                    onClick={() => setReportOpen(true)}>
              <FilePdf size={16} weight="bold" /> {t("pdfReports")}
            </Button>
            <a href="/hse/field"
               className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-bold text-slate-800 border border-slate-300 hover:bg-slate-100"
               data-testid="hse-field-link">
              🚩 {t("hseFieldReport")}
            </a>
          </>
        }
      />

      <HseReportDialog open={reportOpen} onOpenChange={setReportOpen} />

      <Tabs defaultValue="dashboard">
        {/* Iter120: TabsList por grupo — cada bloque en su propia línea con
            etiqueta de sección para ordenar la vista. */}
        <div className="space-y-2" data-testid="hse-groups">
          {TAB_GROUPS.map((grp) => (
            <div key={grp.key} className="flex items-center gap-2 flex-wrap" data-testid={`hse-group-${grp.key}`}>
              <span
                className={`shrink-0 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded border ${grp.color}`}
                title={grp.label}
              >
                {grp.label}
              </span>
              <TabsList className="bg-slate-100 flex-wrap h-auto gap-1 p-1">
                {grp.tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.v}
                    value={tab.v}
                    data-testid={`hse-tab-${tab.v}`}
                    className="text-slate-600 font-semibold data-[state=active]:bg-white data-[state=active]:text-blue-900 data-[state=active]:shadow-sm hover:text-slate-900 hover:bg-white/60 transition"
                  >
                    {t(tab.l)}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
          ))}
        </div>
        {TABS.map((tab) => (
          <TabsContent key={tab.v} value={tab.v} className="mt-4">
            <tab.C />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
